import { useState, useEffect, useCallback, useRef } from 'react';
import useWebSocket from './useWebSocket';
import useAuth from './useAuth';
import { api } from '../services/api';
import { appConfig } from '../config/appConfig';

const useNotificationService = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  
  // Get WebSocket connection params
  const wsUrl = appConfig.ws.getUrl();
  const token = localStorage.getItem('token');
  const tenantId = user?.tenantId || localStorage.getItem('tenantId');
  
  // Use updated WebSocket hook with explicit connection params
  const { socket, isConnected, subscribe, reconnect } = useWebSocket(wsUrl, { 
    token, 
    tenantId 
  });

  // Add fetching flags to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const THROTTLE_MS = 5000; // 5 seconds between fetches
  
  // Define notification types for each category
  const WORK_NOTIFICATION_TYPES = ['task_assignment', 'task_response', 'task_update', 'task_mention'];
  const ENGAGE_NOTIFICATION_TYPES = [
    'post_like', 'post_comment', 'post_mention', 
    'comment_like', 'comment_reply',
    'group_invitation', 'group_join_request', 'group_join_approved', 'group_role_change',
    'poll_vote', 'new_group_post', 'event_mention', 'event_invitation', 'event_response',
    'event_update', 'event_cancelled'
  ];
  const TALK_NOTIFICATION_TYPES = ['chat_invitation'];

  // Get notification counts by type (work, engage, talk)
  const getNotificationCountsByType = useCallback(() => {
    // Default empty counts
    const counts = {
      work: 0,
      engage: 0, 
      talk: 0
    };
    
    // Only count unread notifications
    const unreadNotifications = notifications.filter(notification => !notification.isSeen);
    
    // Count by category
    unreadNotifications.forEach(notification => {
      const type = notification.type;
      
      if (WORK_NOTIFICATION_TYPES.includes(type)) {
        counts.work += 1;
      } else if (ENGAGE_NOTIFICATION_TYPES.includes(type)) {
        counts.engage += 1;
      } else if (TALK_NOTIFICATION_TYPES.includes(type)) {
        counts.talk += 1;
      }
    });
    
    return counts;
  }, [notifications]);

  // Debug logging for connection (removed)

  useEffect(() => {
    // No-op: removed connection debug logs
  }, [isConnected]);

  // Fetch notifications
  const fetchNotifications = useCallback(async (params = {}) => {
    // Prevent multiple concurrent fetches and throttle
    const now = Date.now();
    if (isFetchingRef.current || (now - lastFetchTimeRef.current < THROTTLE_MS && !params.page)) {
      // removed: console log for skipping fetch
      return null;
    }
    
    try {
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;
      setLoading(true);
      setError(null);
      const { data } = await api.get('/otms/notification/info', { params });
      
      if (params.page && params.page > 1) {
        // Append new notifications for pagination
        setNotifications(prev => [...prev, ...data.notifications]);
      } else {
        // Replace notifications for initial load or refresh
        setNotifications(data.notifications);
      }
      setUnreadCount(data.unreadCount);
      return data;
    } catch (err) {
      // removed: console.error
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Mark notifications as seen
  const markAsSeen = useCallback(async (notificationIds) => {
    if (!notificationIds || notificationIds.length === 0) return null;
    
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put('/otms/notification/seen', { notificationIds });
      
      // Update local state immediately
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notificationIds.includes(notification.id) 
            ? { ...notification, isSeen: true }
            : notification
        )
      );
      setUnreadCount(data.unreadCount);

      return data;
    } catch (err) {
      // removed: console.error
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!notificationId) {
      // removed: console.error for missing ID
      return false;
    }
    
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.delete(`/otms/notification/delete/${notificationId}`);
      
      // Update local state immediately to ensure UI is in sync
      setNotifications(prevNotifications => {
        const filtered = prevNotifications.filter(notification => notification.id !== notificationId);
        // removed: console.log for deleted notification
        return filtered;
      });
      
      if (data && data.unreadCount !== undefined) {
        setUnreadCount(data.unreadCount);
      }

      return true;
    } catch (err) {
      // removed: console.error for failed delete
      setError(`Failed to delete notification: ${err.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle socket events
  useEffect(() => {
    if (isConnected && user) {
      
      // Join user's global notification room
      const userRoom = `notifications-${user.tenantId}-${user.personId}`;
      const tenantRoom = `tenant-notifications-${user.tenantId}`;
      let hasJoinedRooms = false;

      const joinRooms = () => {
        if (hasJoinedRooms) return; // Prevent duplicate joins
        
        // Join user-specific room
        socket.emit('join-room', userRoom, (error) => {
          if (error) {
            // removed: console.error for join user room
          } else {
            // removed: console.log for join user room
            hasJoinedRooms = true;
          }
        });

        // Join tenant-wide room
        socket.emit('join-room', tenantRoom, (error) => {
          if (error) {
            // removed: console.error for join tenant room
          } else {
            // removed: console.log for join tenant room
          }
        });
      };

      joinRooms();

      // Enhanced reconnection handler
      const handleReconnect = () => {
        // removed: console.log for websocket reconnected
        hasJoinedRooms = false;
        joinRooms();
        
        // Refresh notifications but with throttling
        const now = Date.now();
        if (now - lastFetchTimeRef.current > THROTTLE_MS) {
          fetchNotifications().catch(
            () => {
              // removed: error logging for fetch after reconnect
            }
          );
        }
      };

      // Subscribe to notification events with enhanced error handling
      const unsubscribeCreate = subscribe('notification-created', (notification) => {
        // removed: console.log for notification-created event
        if (notification.userId === user.personId) {
          setNotifications(prev => {
            const exists = prev.some(n => n.id === notification.id);
            if (!exists) {
              try {
                const audio = new Audio('/notification.mp3');
                audio.play().catch(() => {
                  // removed: error log for audio play
                });
              } catch (error) {
                // removed: error log for notification sound
              }
              return [notification, ...prev];
            }
            return prev;
          });
          if (!notification.isSeen) {
            setUnreadCount(prev => prev + 1);
          }
        }
      });

      // Throttle the notification update event with debouncing
      let updateTimeoutId = null;
      
      // Subscribe to notification events
      const unsubscribeUpdate = subscribe('notification-update', () => {
        // removed: console.log for notification-update event
        
        // Debounce multiple update events
        if (updateTimeoutId) {
          clearTimeout(updateTimeoutId);
        }
        
        updateTimeoutId = setTimeout(() => {
          const now = Date.now();
          if (now - lastFetchTimeRef.current > THROTTLE_MS) {
            fetchNotifications();
          }
        }, 1000); // Wait 1 second before refreshing
      });

      const unsubscribeSeen = subscribe('notification-seen', ({ notificationIds, unreadCount: newUnreadCount }) => {
        // removed: console.log for notification-seen event
        setNotifications(prev => 
          prev.map(notification => 
            notificationIds.includes(notification.id) 
              ? { ...notification, isSeen: true }
              : notification
          )
        );
        setUnreadCount(newUnreadCount);
      });

      const unsubscribeDeleted = subscribe('notification-deleted', ({ notificationId, unreadCount: newUnreadCount }) => {
        // removed: console.log for notification-deleted event
        setNotifications(prev => 
          prev.filter(notification => notification.id !== notificationId)
        );
        setUnreadCount(newUnreadCount);
      });

      // Handle task-specific notifications
      const unsubscribeTaskNotification = subscribe('task-notification', (data) => {
        // removed: console.log for task-notification event
        
        // Throttle the refresh
        const now = Date.now();
        if (now - lastFetchTimeRef.current > THROTTLE_MS) {
          fetchNotifications();
        }
      });

      socket.on('reconnect', handleReconnect);
      socket.on('connect_error', (error) => {
        // removed: console.error for socket connection error
      });

      return () => {
        if (hasJoinedRooms) {
          socket.emit('leave-room', userRoom, (error) => {
            if (error) {
              // removed: console.error for leaving user notification room
            } else {
              // removed: console.log for leaving user notification room
            }
          });
          socket.emit('leave-room', tenantRoom, (error) => {
            if (error) {
              // removed: console.error for leaving tenant notification room
            } else {
              // removed: console.log for leaving tenant notification room
            }
          });
        }
        
        if (updateTimeoutId) {
          clearTimeout(updateTimeoutId);
        }
        
        unsubscribeCreate();
        unsubscribeUpdate();
        unsubscribeSeen();
        unsubscribeDeleted();
        unsubscribeTaskNotification();
        socket.off('reconnect', handleReconnect);
        socket.off('connect_error');
      };
    }
  }, [isConnected, user, socket, subscribe, fetchNotifications]);

  // Initial fetch and periodic refresh - IMPORTANT: Avoid infinite loops here
  useEffect(() => {
    if (user && !isFetchingRef.current) {
      fetchNotifications();

      // Create the refresh interval with a longer period (60 seconds) to prevent excessive queries
      const refreshInterval = setInterval(() => {
        if (isConnected && !isFetchingRef.current) {
          const now = Date.now();
          if (now - lastFetchTimeRef.current > THROTTLE_MS) {
            // removed: console.log for periodic notification refresh
            fetchNotifications();
          }
        }
      }, 60000); // Much longer interval (1 minute)

      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [user, fetchNotifications, isConnected]);

  // Attempt to reconnect if connection fails, but don't trigger rerenders
  useEffect(() => {
    let reconnectAttempt = null;
    
    if (!isConnected && token && tenantId && user) {
      reconnectAttempt = setTimeout(() => {
        // removed: console.log for attempting reconnect
        reconnect();
      }, 10000); // Wait longer (10 seconds) between reconnection attempts
    }
    
    return () => {
      if (reconnectAttempt) {
        clearTimeout(reconnectAttempt);
      }
    };
  }, [isConnected, token, tenantId, user, reconnect]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsSeen,
    deleteNotification,
    setNotifications,
    setUnreadCount,
    getNotificationCountsByType
  };
};

export default useNotificationService; 